import { roundOre } from './money.js';
/**
 * Danish home-borrowing rules. All figures are ⚠ baseline — see
 * finance-app/danish-personal-finance-rules.md; verify against Finanstilsynet /
 * Lov om realkredit before presenting as authoritative. Formulas here are stable.
 */
/** Gældsfaktor = total household debt ÷ gross ANNUAL household income. */
export function gaeldsfaktor(totalDebt, grossAnnualIncome) {
    if (grossAnnualIncome <= 0)
        throw new Error('grossAnnualIncome must be > 0');
    return totalDebt / grossAnnualIncome;
}
/**
 * Purchasing power (Købekraft m. gældsfaktor):
 *   max_debt = maxFactor × gross_annual_income − existing_debt
 * Finanstilsynet: >4 is "high", 5 is the practical ceiling.
 */
export function purchasingPower(grossAnnualIncome, existingDebt, maxFactor = 4.0) {
    return roundOre(maxFactor * grossAnnualIncome - existingDebt);
}
/** Statutory realkredit loan-to-value cap by property type (Lov om realkredit §5). */
export function realkreditLtvCap(type) {
    switch (type) {
        case 'ejerbolig':
            return 0.8; // owner-occupied year-round home
        case 'fritidshus':
            return 0.75; // holiday home
        case 'landbrug':
            return 0.7; // agriculture
        case 'erhverv':
            return 0.6; // commercial
    }
}
export const MIN_UDBETALING = 0.05; // minimum 5% own payment (god-skik)
/**
 * Split a purchase price into udbetaling / realkredit / bank-loan per the statutory caps.
 * realkredit up to the type cap; the remainder above the cap (after the 5% udbetaling)
 * is the higher-rate bank loan (boliglån).
 */
export function financingSplit(price, type, udbetalingRate = MIN_UDBETALING) {
    const rate = Math.max(udbetalingRate, MIN_UDBETALING);
    const udbetaling = roundOre(price * rate);
    const realkredit = roundOre(price * realkreditLtvCap(type));
    const financed = price - udbetaling;
    const bankloan = Math.max(0, financed - realkredit);
    return { udbetaling, realkredit: Math.min(realkredit, financed), bankloan };
}
/** Belåningsgrad (LTV) = secured loan ÷ property value. */
export function belaaningsgrad(loan, propertyValue) {
    if (propertyValue <= 0)
        throw new Error('propertyValue must be > 0');
    return loan / propertyValue;
}
export function raadighedsbeloeb(input) {
    const adults = input.adults ?? 1;
    const children = input.children ?? 0;
    const perAdult = input.perAdultMin ?? 600000; // ~6.000 kr in øre (⚠ guideline)
    const perChild = input.perChildMin ?? 300000; // ~3.000 kr in øre (⚠ guideline)
    const disposable = input.netMonthlyIncome - input.fixedMonthlyExpenses;
    const requiredMin = adults * perAdult + children * perChild;
    return { disposable, requiredMin, passes: disposable >= requiredMin };
}
/**
 * Bidragssats (realkredit contribution fee) as a fraction of the outstanding realkredit
 * loan, scaling with the loan's LTV band. Baseline ⚠ bands (Totalkredit-style tiers):
 * ≤40% ≈ 0.40%, ≤60% ≈ 0.55%, ≤80% ≈ 0.85% p.a. See danish-personal-finance-rules.
 */
export function bidragssats(ltv) {
    if (ltv <= 0.4)
        return 0.004;
    if (ltv <= 0.6)
        return 0.0055;
    return 0.0085;
}
/** Danish deed/mortgage registration tax: fixed fee + 1.25% of the mortgage. */
export const TINGLYSNING_FIXED = 185000; // 1.850 kr in øre (deed fee). NOTE: research notes pant-fixed = 1.825 kr — deed-vs-pant modelling nuance; flag for future review.
export const TINGLYSNING_VARIABLE_RATE = 0.0125; // 1.25% pant rate, eff. 1 Jan 2026 (Skatteministeriet); see finance-app/danish-personal-finance-rules.md
export function oneTimeCosts(input) {
    const tinglysningVariable = roundOre(input.mortgage * TINGLYSNING_VARIABLE_RATE);
    return {
        tinglysningFixed: TINGLYSNING_FIXED,
        tinglysningVariable,
        total: TINGLYSNING_FIXED + tinglysningVariable,
    };
}
/** Model a named purchase scenario (sheet: "buy + teardown + rebuild − savings" etc.). */
export function purchaseScenario(s) {
    const gross = s.costs.reduce((a, b) => a + b, 0);
    return { name: s.name, gross, savings: s.savings, needed: Math.max(0, gross - s.savings) };
}
//# sourceMappingURL=danish.js.map